// Browser-side equivalent of the old export-project-bundle edge function.
// Manifest-shaped — no recursive embedding of per-site files; just CSVs and
// a clickable HTML index.

import JSZip from "jszip";
import type { BuildContext } from "@/features/exports/services/DownloadManagerContext";
import { supabase } from "@/shared/integrations/supabase/client";
import { selectWithSurveySource } from "@/shared/integrations/supabase/surveySource";
import { fetchClientProperties } from "@/features/projects/services/projectsApi";
import { csvCell, getAppBaseUrl } from "./shared";
import { sanitize } from "@/features/exports/services/_helpers";
import { resolveSurveyAttributes } from "@/features/site-survey-editor/services/resolveSurveyAttributes";

interface Args {
  projectId: string;
}

interface PropertyRow {
  id: string;
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  store_number: string | null;
  square_footage: number | string | null;
}

export async function buildProjectBundle({ projectId }: Args, ctx: BuildContext): Promise<Blob> {
  const startedAt = new Date().toISOString();

  ctx.setProgress(2, "Loading project…");

  const [{ data: project }, authorizedProperties] = await Promise.all([
    supabase.from("projects").select("id, name").eq("id", projectId).maybeSingle(),
    fetchClientProperties<PropertyRow>(
      projectId,
      "id, name, address, city, state, zip_code, store_number, square_footage",
    ),
  ]);

  // store_number ascending, nulls last (matches the prior DB ordering)
  const propertyList = authorizedProperties.sort((a, b) => {
    if (a.store_number == null && b.store_number == null) return 0;
    if (a.store_number == null) return 1;
    if (b.store_number == null) return -1;
    return String(a.store_number).localeCompare(String(b.store_number), undefined, {
      numeric: true,
    });
  });
  const projectName = sanitize(project?.name ?? "project");
  const projectLabel = project?.name ?? "Project";

  const zip = new JSZip();
  const root = zip.folder(projectName)!;

  root.file(
    "INFO.txt",
    [
      `${projectLabel} — Project Bundle`,
      `Generated: ${startedAt}`,
      `Properties: ${propertyList.length}`,
      ``,
      `Contents:`,
      `  README.html              Clickable index of properties (deep links into the app)`,
      `  cross_store_survey.csv   Per-question answer rows for every property`,
      `  cross_store_assets.csv   Asset register rows for every property`,
      `  properties.csv           Property metadata (store #, address, etc.)`,
      ``,
      `Per-site digital twin files and photos are not embedded here — they are`,
      `available as per-property "Site bundle" downloads via the links in README.html.`,
    ].join("\n"),
  );

  // properties.csv
  ctx.setProgress(10, "Building properties.csv…");
  root.file("properties.csv", buildPropertiesCsv(propertyList, projectId));

  // cross_store_survey.csv
  ctx.setProgress(25, "Loading survey data…");
  root.file("cross_store_survey.csv", await buildCrossStoreSurveyCsv(propertyList));

  // cross_store_assets.csv
  ctx.setProgress(70, "Loading asset data…");
  root.file("cross_store_assets.csv", await buildCrossStoreAssetsCsv(projectId, propertyList));

  // README.html
  ctx.setProgress(92, "Building HTML index…");
  root.file("README.html", buildIndexHtml(projectLabel, propertyList, projectId));

  ctx.setProgress(96, "Compressing…");
  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
  ctx.setProgress(100, "Done");
  return blob;
}

function buildPropertiesCsv(properties: PropertyRow[], _projectId: string): string {
  const appBase = getAppBaseUrl();
  const header = ["Store #", "Name", "Address", "City", "State", "ZIP", "Square Footage", "Property URL"].map(csvCell).join(",");
  const rows = [header];
  for (const p of properties) {
    rows.push([
      csvCell(p.store_number),
      csvCell(p.name),
      csvCell(p.address),
      csvCell(p.city),
      csvCell(p.state),
      csvCell(p.zip_code),
      csvCell(p.square_footage?.toString() ?? ""),
      csvCell(`${appBase}/property/${p.id}`),
    ].join(","));
  }
  return rows.join("\n");
}

async function buildCrossStoreSurveyCsv(properties: PropertyRow[]): Promise<string> {
  const header = ["Property", "Property ID", "Store #", "Category", "Question", "Answer"].map(csvCell).join(",");
  const rows = [header];
  if (properties.length === 0) return rows.join("\n");

  const propertyIds = properties.map((p) => p.id);
  const PAGE = 500;
  const latest = new Map<string, { data: unknown; prod_data?: unknown; survey_source?: string | null; captured_at: string }>();

  // Include survey_source only when the column exists (migration-safe).
  const attrCols = await selectWithSurveySource("property_id, data, prod_data, captured_at");
  for (let i = 0; i < propertyIds.length; i += PAGE) {
    const chunk = propertyIds.slice(i, i + PAGE);
    const { data, error } = await supabase
      .from("property_attributes")
      .select(attrCols)
      .in("property_id", chunk)
      .order("captured_at", { ascending: false });
    if (error) throw error; // don't silently leave `latest` empty on a failed lookup
    for (const row of (data ?? []) as unknown as Array<{ property_id: string; data: unknown; prod_data?: unknown; survey_source?: string | null; captured_at: string }>) {
      if (!latest.has(row.property_id)) latest.set(row.property_id, row);
    }
  }

  for (const p of properties) {
    const label = p.name || p.address || `Prop-${p.id.slice(0, 4)}`;
    const attr = latest.get(p.id);
    // Resolve through the gate (self-describing prod_data when flagged, else
    // legacy data). Don't gate on `data` alone — a prod_data-backed row can have
    // no/empty `data` yet still render from prod_data.
    let fields: Array<Record<string, unknown>> = [];
    if (attr) {
      try {
        const dataTree = typeof attr.data === "string" ? JSON.parse(attr.data) : (attr.data ?? []);
        fields = resolveSurveyAttributes({
          surveySource: attr.survey_source,
          prodData: (attr.prod_data as Record<string, unknown> | null) ?? null,
          data: Array.isArray(dataTree) ? dataTree : [],
        }).fields as unknown as Array<Record<string, unknown>>;
      } catch {
        fields = [];
      }
    }
    if (fields.length === 0) {
      rows.push([csvCell(label), csvCell(p.id), csvCell(p.store_number ?? ""), "", "", ""].join(","));
      continue;
    }

    function walk(field: Record<string, unknown>, parent: string) {
      const children = field.fields as Array<Record<string, unknown>> | undefined;
      if (Array.isArray(children) && children.length > 0) {
        const newParent = parent ? `${parent} > ${field.title}` : String(field.title);
        for (const c of children) walk(c, newParent);
        return;
      }
      const cleanTitle = String(field.title ?? "").replace(/\s*\[.*?\]\s*$/, "");
      let answer = "";
      const t = field.type;
      const v = field.value;
      if (t === "photos") {
        answer = Array.isArray(v) && v.length > 0 ? `${v.length} photo(s) attached` : "";
      } else if (t === "area") {
        answer = typeof v === "string" ? v.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";
      } else if (t === "multiSelect") {
        answer = Array.isArray(v) && v.length > 0 ? v.join("; ") : "";
      } else {
        answer = v == null ? "" : String(v);
      }
      const category = parent.split(" > ")[0] || "General";
      rows.push([
        csvCell(label), csvCell(p.id), csvCell(p.store_number ?? ""),
        csvCell(category), csvCell(cleanTitle), csvCell(answer),
      ].join(","));
    }
    for (const f of fields) walk(f, "");
  }
  return rows.join("\n");
}

interface CrossStoreAssetRow {
  id: string;
  property_id: string;
  name: string | null;
  description: string | null;
  category_id: string | null;
  asset_categories: { name: string } | { name: string }[] | null;
  manufacturer: string | null;
  model_name: string | null;
  model_number: string | null;
  serial_number: string | null;
  manufacture_date: string | null;
  estimated_age_years: number | null;
  age_source: string | null;
  condition_status: string | null;
  zone_id: string | null;
  project_zones: { name: string } | { name: string }[] | null;
  nameplate_photo_url: string | null;
  asset_photo_url: string | null;
  category_attributes: Record<string, unknown> | null;
  notes: string | null;
}

/** Flatten category_attributes JSONB (tonnage, voltage, etc.) into one readable cell. */
function flattenAttrs(attrs: Record<string, unknown> | null): string {
  if (!attrs || typeof attrs !== "object") return "";
  return Object.entries(attrs)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}: ${String(v)}`)
    .join("; ");
}

async function buildCrossStoreAssetsCsv(_projectId: string, properties: PropertyRow[]): Promise<string> {
  const appBase = getAppBaseUrl();
  const header = [
    "Property", "Store #", "Asset Name", "Category", "Description", "Manufacturer",
    "Model Name", "Model Number", "Serial Number", "Manufacture Date",
    "Estimated Age (Years)", "Age Source", "Condition Status", "Location in Store",
    "Attributes", "Notes",
    "Property URL", "Asset URL", "Nameplate Photo", "Asset Photo",
  ].map(csvCell).join(",");
  const rows = [header];

  if (properties.length === 0) return rows.join("\n");

  const propertyMap = new Map<string, { label: string; store_number: string }>();
  for (const p of properties) {
    propertyMap.set(p.id, {
      label: p.name || p.address || `Prop-${p.id.slice(0, 4)}`,
      store_number: p.store_number ?? "",
    });
  }

  const propertyIds = properties.map((p) => p.id);
  const PAGE = 40;
  for (let i = 0; i < propertyIds.length; i += PAGE) {
    const chunk = propertyIds.slice(i, i + PAGE);
    let offset = 0;
    const PAGE_SIZE = 1000;
    while (true) {
      const { data } = await supabase
        .from("property_assets")
        .select(
          "id, name, description, category_id, asset_categories(name), manufacturer, " +
          "model_name, model_number, serial_number, manufacture_date:manufacture_date_string, estimated_age_years, age_source, " +
          "condition_status, zone_id, project_zones(name), nameplate_photo_url, asset_photo_url, " +
          "category_attributes, notes, property_id",
        )
        .in("property_id", chunk)
        .range(offset, offset + PAGE_SIZE - 1);
      if (!data || data.length === 0) break;
      for (const a of (data as unknown as CrossStoreAssetRow[])) {
        const info = propertyMap.get(a.property_id);
        const categoryName = Array.isArray(a.asset_categories) ? a.asset_categories[0]?.name : a.asset_categories?.name;
        const zoneName = Array.isArray(a.project_zones) ? a.project_zones[0]?.name : a.project_zones?.name;
        rows.push([
          csvCell(info?.label ?? ""),
          csvCell(info?.store_number ?? ""),
          csvCell(a.name),
          csvCell(categoryName ?? ""),
          csvCell(a.description),
          csvCell(a.manufacturer),
          csvCell(a.model_name),
          csvCell(a.model_number),
          csvCell(a.serial_number),
          csvCell(a.manufacture_date),
          csvCell(a.estimated_age_years?.toString() ?? ""),
          csvCell(a.age_source),
          csvCell(a.condition_status),
          csvCell(zoneName ?? ""),
          csvCell(flattenAttrs(a.category_attributes)),
          csvCell(a.notes),
          csvCell(`${appBase}/property/${a.property_id}`),
          csvCell(`${appBase}/asset/${a.id}`),
          csvCell(a.nameplate_photo_url),
          csvCell(a.asset_photo_url),
        ].join(","));
      }
      if (data.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }
  }
  return rows.join("\n");
}

function buildIndexHtml(projectLabel: string, properties: PropertyRow[], _projectId: string): string {
  const appBase = getAppBaseUrl();
  const rows = properties.map((p) => {
    const href = `${appBase}/property/${p.id}/exports`;
    const addr = [p.address, p.city, p.state, p.zip_code].filter(Boolean).join(", ");
    return `
      <tr>
        <td>${escapeHtml(p.store_number ?? "")}</td>
        <td>${escapeHtml(p.name ?? "")}</td>
        <td>${escapeHtml(addr)}</td>
        <td><a href="${escapeHtml(href)}" target="_blank" rel="noopener">Open exports →</a></td>
      </tr>`;
  }).join("");

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escapeHtml(projectLabel)} — Project Bundle Index</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; max-width: 1100px; margin: 2rem auto; padding: 0 1rem; color: #222; }
  h1 { margin-bottom: 0.25rem; }
  p.meta { color: #666; margin-top: 0; }
  table { width: 100%; border-collapse: collapse; margin-top: 1.5rem; }
  th, td { border-bottom: 1px solid #e5e5e5; padding: 0.6rem 0.75rem; text-align: left; vertical-align: top; }
  th { background: #fafafa; font-weight: 600; }
  a { color: #0a58ca; text-decoration: none; }
  a:hover { text-decoration: underline; }
  code { background: #f3f3f3; padding: 0.05rem 0.3rem; border-radius: 3px; }
</style>
</head>
<body>
  <h1>${escapeHtml(projectLabel)}</h1>
  <p class="meta">Project bundle generated ${new Date().toISOString()}</p>
  <p>
    This archive contains cross-store data (<code>cross_store_survey.csv</code>, <code>cross_store_assets.csv</code>,
    <code>properties.csv</code>). For per-site digital twin files, photos, and full site bundles, follow the
    "Open exports" links below.
  </p>
  <table>
    <thead>
      <tr><th>Store #</th><th>Name</th><th>Address</th><th>Per-site exports</th></tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;
}

function escapeHtml(s: string | null | undefined): string {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
